import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

export type Milestone = Tables<'milestones'>;
export type MilestoneInsert = TablesInsert<'milestones'>;
export type MilestoneUpdate = TablesUpdate<'milestones'>;

export const useMilestones = (projectId: string | undefined) => {
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useQuery({
    queryKey: ['milestones', projectId, isDemo, isDemo ? demoStore.version : 0],
    queryFn: async () => {
      if (!projectId) return [] as Milestone[];

      if (isDemo) {
        // Wave 8.1a — pre-seeded per-project rows so the timeline
        // reads correctly in Preview Studio. Sort by sort_order.
        return demoStore.milestones
          .list()
          .filter((m) => m.project_id === projectId)
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)) as unknown as Milestone[];
      }

      const { data, error } = await supabase
        .from('milestones')
        .select('*')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as Milestone[];
    },
    enabled: !!projectId,
  });
};

export const useUpdateMilestone = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({ id, projectId, ...updates }: MilestoneUpdate & { id: string; projectId: string }) => {
      if (isDemo) {
        const updated = demoStore.milestones.update(id, updates as Record<string, any>);
        return updated as unknown as Milestone;
      }

      const { data, error } = await supabase
        .from('milestones')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['milestones', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
    },
  });
};

export const useToggleMilestone = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({ id, projectId, completed }: { id: string; projectId: string; completed: boolean }) => {
      if (isDemo) {
        const updated = demoStore.milestones.update(id, { completed });
        return updated as unknown as Milestone;
      }

      const { data, error } = await supabase
        .from('milestones')
        .update({ completed })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['milestones', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', variables.projectId] });
    },
  });
};

/// Add a new milestone to a project. Caller can provide name + optional
/// due_date; we compute sort_order to append at the end of the list.
export const useAddMilestone = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({ projectId, name, due_date }: { projectId: string; name: string; due_date?: string | null }) => {
      if (isDemo) {
        const existing = demoStore.milestones
          .list()
          .filter((m) => m.project_id === projectId);
        const nextOrder = existing.reduce(
          (max, m) => Math.max(max, m.sort_order ?? 0),
          0,
        ) + 1;
        const created = demoStore.milestones.add({
          id: `demo-ms-${crypto.randomUUID().slice(0, 8)}`,
          project_id: projectId,
          name,
          due_date: due_date ?? null,
          completed: false,
          sort_order: nextOrder,
          created_at: new Date().toISOString(),
        });
        return created;
      }

      // Append to the end — fetch the current max sort_order first.
      const { data: existing } = await supabase
        .from('milestones')
        .select('sort_order')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: false })
        .limit(1);
      const nextOrder = (existing?.[0]?.sort_order ?? -1) + 1;

      const { data, error } = await supabase
        .from('milestones')
        .insert({
          project_id: projectId,
          name,
          due_date: due_date ?? null,
          completed: false,
          sort_order: nextOrder,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['milestones', vars.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', vars.projectId] });
    },
  });
};

export const useDeleteMilestone = () => {
  const queryClient = useQueryClient();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();

  return useMutation({
    mutationFn: async ({ id, projectId }: { id: string; projectId: string }) => {
      if (isDemo) {
        demoStore.milestones.remove(id);
        return;
      }
      const { error } = await supabase
        .from('milestones')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['milestones', vars.projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', vars.projectId] });
    },
  });
};
